import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, RefreshCw, Play, Trash2, Gauge, Smartphone, Monitor,
  Plus, ExternalLink, TrendingUp, AlertTriangle, Info, Zap, Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from "recharts";

const STRATEGIES = [
  { key: "mobile", label: "Mobile", icon: Smartphone, color: "#4285F4" },
  { key: "desktop", label: "Desktop", icon: Monitor, color: "#34A853" },
];

const CATEGORY_LABELS = {
  performance: "Performance",
  accessibility: "Accessibility",
  best_practices: "Best Practices",
  seo: "SEO",
};

function scoreColor(score) {
  if (score == null) return "#94a3b8";
  if (score >= 90) return "#0f9d58"; // green
  if (score >= 50) return "#f4b400"; // amber
  return "#db4437"; // red
}

/**
 * Render a Lighthouse description string with `[text](url)` markdown
 * links converted into real clickable anchors that open in a new tab.
 * Uses a plain regex tokenizer — no dangerouslySetInnerHTML — so we
 * stay safe from injected HTML in Google's descriptions.
 */
function LighthouseDescription({ text }) {
  if (!text) return null;
  const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const nodes = [];
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      nodes.push(text.slice(last, m.index));
    }
    nodes.push(
      <a
        key={`lnk-${key++}`}
        href={m[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline decoration-dotted underline-offset-2 inline-flex items-center gap-0.5"
        onClick={(e) => e.stopPropagation()}
        data-testid="psi-issue-learn-link"
      >
        {m[1]}
        <ExternalLink className="h-2.5 w-2.5 opacity-70" />
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return <>{nodes}</>;
}

/**
 * Very small markdown renderer for the Ask-AI answer. Supports
 * headings (`##`, `###`), **bold**, bullets (`- `), numbered lists
 * (`1. `), inline `code`, and blank-line paragraph breaks. Anything
 * else falls through as plain text — keeps us safe from stray HTML
 * without pulling in a full markdown library.
 */
function AiMarkdown({ text }) {
  if (!text) return null;
  const lines = String(text).split(/\r?\n/);
  const out = [];
  let list = null; // { type: "ul"|"ol", items: [] }
  const flushList = () => {
    if (!list) return;
    const Tag = list.type === "ol" ? "ol" : "ul";
    out.push(
      <Tag
        key={`l-${out.length}`}
        className={
          list.type === "ol"
            ? "list-decimal ml-5 space-y-1 my-2 text-[12px] text-zinc-800"
            : "list-disc ml-5 space-y-1 my-2 text-[12px] text-zinc-800"
        }
      >
        {list.items.map((it, i) => (
          <li key={i}>{renderInline(it)}</li>
        ))}
      </Tag>
    );
    list = null;
  };
  const renderInline = (s) => {
    // bold  **x**   →   <strong>x</strong>
    // code  `x`     →   <code>x</code>
    // link  [t](u)  →   <a>...</a>
    const parts = [];
    let rest = s;
    const rx = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^\s)]+\))/;
    let mm;
    let k = 0;
    while ((mm = rx.exec(rest)) !== null) {
      if (mm.index > 0) parts.push(rest.slice(0, mm.index));
      const tok = mm[0];
      if (tok.startsWith("**")) {
        parts.push(
          <strong key={`b-${k++}`}>{tok.slice(2, -2)}</strong>
        );
      } else if (tok.startsWith("`")) {
        parts.push(
          <code
            key={`c-${k++}`}
            className="px-1 py-0.5 rounded bg-zinc-100 font-mono text-[11px] text-zinc-800"
          >
            {tok.slice(1, -1)}
          </code>
        );
      } else {
        const inner = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/.exec(tok);
        if (inner) {
          parts.push(
            <a
              key={`a-${k++}`}
              href={inner[2]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {inner[1]}
            </a>
          );
        }
      }
      rest = rest.slice(mm.index + tok.length);
    }
    if (rest) parts.push(rest);
    return parts;
  };
  for (let idx = 0; idx < lines.length; idx++) {
    const raw = lines[idx];
    const line = raw.replace(/\s+$/, "");
    // Fenced code block  ``` ... ```
    if (/^```/.test(line)) {
      flushList();
      const lang = line.replace(/^```/, "").trim();
      const codeLines = [];
      let j = idx + 1;
      while (j < lines.length && !/^```/.test(lines[j])) {
        codeLines.push(lines[j]);
        j++;
      }
      out.push(
        <pre
          key={`pre-${out.length}`}
          className="my-2 p-2.5 rounded-sm bg-zinc-900 text-zinc-100 text-[11px] font-mono overflow-x-auto whitespace-pre"
        >
          {lang && (
            <div className="text-[9px] uppercase tracking-wider text-zinc-400 mb-1">
              {lang}
            </div>
          )}
          <code>{codeLines.join("\n")}</code>
        </pre>
      );
      idx = j; // jump past the closing fence
      continue;
    }
    if (!line.trim()) { flushList(); continue; }
    let mmHead;
    if ((mmHead = /^(#{2,3})\s+(.*)$/.exec(line))) {
      flushList();
      const level = mmHead[1].length;
      const Tag = level === 2 ? "h4" : "h5";
      out.push(
        <Tag
          key={`h-${out.length}`}
          className={
            level === 2
              ? "text-[13px] font-semibold text-zinc-900 mt-3 mb-1"
              : "text-[12px] font-semibold text-zinc-800 mt-2 mb-1"
          }
        >
          {renderInline(mmHead[2])}
        </Tag>
      );
      continue;
    }
    let mmOl;
    if ((mmOl = /^\s*\d+\.\s+(.*)$/.exec(line))) {
      if (!list || list.type !== "ol") { flushList(); list = { type: "ol", items: [] }; }
      list.items.push(mmOl[1]);
      continue;
    }
    let mmUl;
    if ((mmUl = /^\s*[-*]\s+(.*)$/.exec(line))) {
      if (!list || list.type !== "ul") { flushList(); list = { type: "ul", items: [] }; }
      list.items.push(mmUl[1]);
      continue;
    }
    flushList();
    out.push(
      <p key={`p-${out.length}`} className="text-[12px] text-zinc-800 leading-relaxed my-1.5">
        {renderInline(line)}
      </p>
    );
  }
  flushList();
  return <>{out}</>;
}

/**
 * Client PSI tab — Google PageSpeed Insights runner + history + trend chart.
 *
 * URLs merge two sources:
 *   1. business_units.website_url — auto-pulled, read-only, from the
 *      Properties tab. Each business unit contributes one URL row.
 *   2. custom URLs — added / removed here, saved to ``psi_urls``.
 *
 * "Run test now" fires two PSI HTTP calls (mobile + desktop) with all
 * four Lighthouse categories. Each strategy is one row in history.
 * The trend chart plots the four category scores against the run
 * timestamp, filtered by the currently selected URL + strategy.
 */
export default function ClientPsiPage() {
  const { clientId } = useParams();
  const [urls, setUrls] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // url currently running
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState("mobile");
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [masterState, setMasterState] = useState(null);

  // ── Ask-AI dialog state ─────────────────────────────────────────────
  // `askIssue` holds the opportunity object the user clicked "Ask AI" on.
  // `askFocus` is null while showing the two-button choice, then flips to
  // "wordpress" / "general" and triggers the actual LLM request.
  const [askIssue, setAskIssue] = useState(null);
  const [askFocus, setAskFocus] = useState(null);
  const [askLoading, setAskLoading] = useState(false);
  const [askAnswer, setAskAnswer] = useState("");
  const [askError, setAskError] = useState("");

  // Historical scan detail viewer — clicking any row in the history table
  // fetches the full stored Lighthouse report and renders it in a dialog.
  const [detailTest, setDetailTest] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailShowRaw, setDetailShowRaw] = useState(false);

  const openTestDetail = async (t) => {
    setDetailTest({ ...t, _loading: true });
    setDetailShowRaw(false);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/psi/tests/${t.test_id}`);
      setDetailTest(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load scan details");
      setDetailTest(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openAskAi = (opp) => {
    setAskIssue(opp);
    setAskFocus(null);
    setAskAnswer("");
    setAskError("");
    setAskLoading(false);
  };

  const runAskAi = async (focus) => {
    if (!askIssue) return;
    setAskFocus(focus);
    setAskLoading(true);
    setAskAnswer("");
    setAskError("");
    try {
      const { data } = await api.post("/psi/ask-ai", {
        issue_id: askIssue.id,
        issue_title: askIssue.title,
        issue_description: askIssue.description || "",
        display_value: askIssue.display_value || null,
        savings_ms: askIssue.savings_ms || null,
        strategy: selectedStrategy,
        url: selectedUrl,
        focus,
      });
      setAskAnswer(data.answer || "");
    } catch (e) {
      setAskError(e.response?.data?.detail || "AI helper failed. Try again.");
    } finally {
      setAskLoading(false);
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: urlData }, { data: testData }] = await Promise.all([
        api.get(`/psi/clients/${clientId}/urls`),
        api.get(`/psi/clients/${clientId}/tests`),
      ]);
      const urlList = urlData.urls || [];
      setUrls(urlList);
      setTests(testData.tests || []);
      // Auto-select the first URL if nothing is picked yet.
      setSelectedUrl((prev) => {
        if (prev && urlList.some((u) => u.url === prev)) return prev;
        return urlList[0]?.url || null;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load PSI data");
    } finally { setLoading(false); }
  }, [clientId]);

  const loadMaster = useCallback(async () => {
    try {
      const { data } = await api.get("/psi/master");
      setMasterState(data);
    } catch { /* silent — admin might not have visited master page yet */ }
  }, []);

  useEffect(() => { loadAll(); loadMaster(); }, [loadAll, loadMaster]);

  const addUrl = async () => {
    if (!newUrl.trim()) { toast.error("Enter a URL"); return; }
    setAdding(true);
    try {
      const { data } = await api.post(`/psi/clients/${clientId}/urls`, {
        url: newUrl.trim(),
        label: newLabel.trim() || null,
      });
      setUrls(data.urls || []);
      setNewUrl(""); setNewLabel("");
      toast.success("URL added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add URL");
    } finally { setAdding(false); }
  };

  const removeUrl = async (u) => {
    if (u.source === "business_unit") {
      toast.info("Business-unit URLs are managed on the Properties tab.");
      return;
    }
    if (!window.confirm(`Remove ${u.url} from the PSI list? Existing history stays.`)) return;
    try {
      const { data } = await api.delete(
        `/psi/clients/${clientId}/urls/${u.url_id}`);
      setUrls(data.urls || []);
      if (selectedUrl === u.url) {
        setSelectedUrl(data.urls?.[0]?.url || null);
      }
      toast.success("URL removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const runTest = async (u) => {
    setRunning(u.url);
    // Fire the run + a polling fallback in parallel. PSI runs can take
    // 60-120s (Google runs a full Lighthouse crawl server-side); some
    // edge proxies cut connections around 100s so we poll the tests
    // endpoint every 10s while the run is in flight — whichever comes
    // back first with new rows wins.
    let stopped = false;
    const initialTestIds = new Set(
      tests.filter((t) => t.url === u.url).map((t) => t.test_id));
    const poll = async () => {
      for (let i = 0; i < 24 && !stopped; i++) {
        // 24 × 10s = 4 min max poll window.
        await new Promise((r) => setTimeout(r, 10000));
        if (stopped) return;
        try {
          const { data: testData } = await api.get(
            `/psi/clients/${clientId}/tests`);
          const rows = testData.tests || [];
          const newRows = rows.filter(
            (t) => t.url === u.url && !initialTestIds.has(t.test_id));
          if (newRows.length > 0) {
            setTests(rows);
            setSelectedUrl(u.url);
            if (newRows.length >= 2) return;   // both strategies landed
          }
        } catch { /* ignore poll errors */ }
      }
    };
    const pollPromise = poll();

    try {
      const { data } = await api.post(`/psi/clients/${clientId}/run`, {
        url: u.url,
      });
      stopped = true;
      if (data.errors?.length) {
        toast.warning(`Ran with ${data.errors.length} error(s): ${data.errors[0].detail}`);
      } else {
        toast.success(`Ran ${data.results?.length || 0} PSI test(s) for ${u.url}`);
      }
      const { data: testData } = await api.get(
        `/psi/clients/${clientId}/tests`);
      setTests(testData.tests || []);
      setSelectedUrl(u.url);
    } catch (e) {
      // If the request timed out but polling picked up rows, don't
      // alarm the operator — the data landed anyway.
      await pollPromise;
      const { data: testData } = await api.get(
        `/psi/clients/${clientId}/tests`).catch(() => ({ data: { tests } }));
      const rows = testData.tests || [];
      const gotNew = rows.some(
        (t) => t.url === u.url && !initialTestIds.has(t.test_id));
      if (gotNew) {
        toast.success("PSI test completed (via background poll)");
        setTests(rows);
        setSelectedUrl(u.url);
      } else {
        toast.error(e.response?.data?.detail || "PSI run failed");
      }
    } finally {
      stopped = true;
      setRunning(null);
    }
  };

  const deleteTest = async (t) => {
    if (!window.confirm("Delete this test row from history?")) return;
    try {
      await api.delete(`/psi/tests/${t.test_id}`);
      setTests((rows) => rows.filter((r) => r.test_id !== t.test_id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  // Filter tests to the selected URL — the chart + history table both
  // key off this. Strategy is a separate filter applied only to the chart.
  const testsForUrl = useMemo(
    () => tests.filter((t) => t.url === selectedUrl),
    [tests, selectedUrl],
  );

  const latestRun = useMemo(() => {
    const rows = testsForUrl.filter((t) => t.strategy === selectedStrategy);
    return rows[0] || null; // list is sorted DESC on the backend
  }, [testsForUrl, selectedStrategy]);

  const chartData = useMemo(() => {
    // Reverse to ASC order for time-series rendering.
    const rows = testsForUrl
      .filter((t) => t.strategy === selectedStrategy)
      .slice().reverse();
    return rows.map((r) => ({
      ts: new Date(r.run_at).toLocaleDateString(undefined,
        { day: "numeric", month: "short", year: "2-digit" }),
      run_at: r.run_at,
      performance: r.scores?.performance ?? null,
      accessibility: r.scores?.accessibility ?? null,
      best_practices: r.scores?.best_practices ?? null,
      seo: r.scores?.seo ?? null,
    }));
  }, [testsForUrl, selectedStrategy]);

  const trend = useMemo(() => {
    // Compare latest run vs. the run before that on the same strategy.
    const rows = testsForUrl.filter((t) => t.strategy === selectedStrategy);
    if (rows.length < 2) return null;
    const a = rows[0].scores || {};
    const b = rows[1].scores || {};
    const delta = {};
    for (const k of Object.keys(CATEGORY_LABELS)) {
      if (a[k] != null && b[k] != null) delta[k] = a[k] - b[k];
    }
    return delta;
  }, [testsForUrl, selectedStrategy]);

  return (
    <div className="space-y-6" data-testid="client-psi-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-[#4285F4]/10 grid place-items-center">
            <Gauge className="h-5 w-5 text-[#4285F4]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">
              Google PageSpeed Insights
            </h1>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Per-property PSI tests, history + trend charts. A monthly
              scheduled rollup keeps history flowing without manual runs.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}
          className="rounded-sm" data-testid="psi-reload-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                   : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Reload
        </Button>
      </div>

      {/* Master-key warning — only if not configured */}
      {masterState && !masterState.configured && (
        <Card className="rounded-sm border-amber-300 bg-amber-50"
          data-testid="psi-no-key-warning">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-[12px] text-amber-900 leading-relaxed">
              <strong>Google PSI API key not configured.</strong>{" "}
              Tests will still run against Google&apos;s low-quota anonymous pool,
              but you&apos;ll hit rate limits quickly. Set the master key on{" "}
              <a href="/integrations/psi"
                className="underline font-semibold hover:text-amber-950">
                Integrations → Google PSI
              </a>.
            </div>
          </CardContent>
        </Card>
      )}

      {/* URL list + run + add */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <div className="label-overline">Properties to test</div>
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
              {urls.length}
            </Badge>
          </div>

          {loading ? (
            <div className="text-center py-6 text-[12px] text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…
            </div>
          ) : urls.length === 0 ? (
            <div className="text-center py-6 text-[12px] text-zinc-500 border
              border-dashed border-zinc-300 rounded-sm">
              No property URLs yet. Add one below, or set a{" "}
              <em>Website URL</em> on any property in the Properties tab.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
              {urls.map((u) => {
                const hasHistory = tests.some((t) => t.url === u.url);
                const isSelected = selectedUrl === u.url;
                return (
                  <div key={u.url_id}
                    className={`px-3 py-2.5 flex items-center gap-3 hover:bg-zinc-50
                      cursor-pointer transition-colors ${isSelected ? "bg-blue-50/50" : ""}`}
                    onClick={() => setSelectedUrl(u.url)}
                    data-testid={`psi-url-row-${u.url_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <a href={u.url} target="_blank" rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="font-mono text-[12px] text-zinc-800 hover:text-[#4285F4]
                            truncate max-w-[380px] inline-flex items-center gap-1">
                          {u.url}
                          <ExternalLink className="h-2.5 w-2.5 opacity-50" />
                        </a>
                        <Badge className={`rounded-sm text-[9px] font-mono ${
                          u.source === "business_unit"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-sky-100 text-sky-800"}`}>
                          {u.source === "business_unit" ? "PROPERTY" : "CUSTOM"}
                        </Badge>
                        {u.label && (
                          <span className="text-[10px] text-zinc-500 truncate">
                            {u.label}
                          </span>
                        )}
                        {!hasHistory && (
                          <span className="text-[9px] font-mono text-zinc-400 uppercase tracking-wider">
                            No history
                          </span>
                        )}
                      </div>
                    </div>
                    <Button size="sm" variant="outline"
                      onClick={(e) => { e.stopPropagation(); runTest(u); }}
                      disabled={running === u.url}
                      className="rounded-sm"
                      title="Runs mobile + desktop tests in parallel. Google runs a full Lighthouse audit server-side — expect 30-90 seconds."
                      data-testid={`psi-run-btn-${u.url_id}`}>
                      {running === u.url
                        ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Running…</>
                        : <><Play className="h-3.5 w-3.5 mr-1" /> Run test</>}
                    </Button>
                    {u.source === "custom" && (
                      <Button size="sm" variant="ghost"
                        onClick={(e) => { e.stopPropagation(); removeUrl(u); }}
                        className="rounded-sm text-red-600 hover:bg-red-50 p-1.5"
                        data-testid={`psi-remove-url-${u.url_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add custom URL */}
          <div className="pt-3 border-t border-zinc-100">
            <div className="label-overline mb-2">Add another URL to test</div>
            <div className="flex gap-2 flex-wrap">
              <Input value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://example.com/pricing"
                className="rounded-sm font-mono text-[12px] flex-1 min-w-[260px]"
                data-testid="psi-new-url-input" />
              <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Optional label"
                className="rounded-sm text-[12px] w-40"
                data-testid="psi-new-url-label" />
              <Button onClick={addUrl} disabled={adding || !newUrl.trim()}
                className="rounded-sm bg-[#4285F4] hover:bg-[#3367D6] text-white"
                data-testid="psi-add-url-btn">
                {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <><Plus className="h-3.5 w-3.5 mr-1" /> Add</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Strategy toggle + scores + chart — only if a URL is selected */}
      {selectedUrl && (
        <>
          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div>
                  <div className="label-overline">Selected URL</div>
                  <div className="font-mono text-[12px] text-zinc-800 truncate max-w-[500px]">
                    {selectedUrl}
                  </div>
                </div>
                <div className="flex items-center gap-1 border border-zinc-200 rounded-sm p-0.5"
                  data-testid="psi-strategy-toggle">
                  {STRATEGIES.map((s) => (
                    <button key={s.key}
                      onClick={() => setSelectedStrategy(s.key)}
                      className={`px-3 py-1.5 text-[11px] font-mono rounded-sm flex items-center gap-1.5
                        transition-colors ${
                          selectedStrategy === s.key
                            ? "bg-zinc-900 text-white"
                            : "text-zinc-600 hover:bg-zinc-100"}`}
                      data-testid={`psi-strategy-${s.key}`}>
                      <s.icon className="h-3 w-3" />
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {latestRun ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5"
                    data-testid="psi-score-cards">
                    {Object.entries(CATEGORY_LABELS).map(([k, label]) => {
                      const score = latestRun.scores?.[k];
                      const delta = trend?.[k];
                      const color = scoreColor(score);
                      return (
                        <div key={k} className="rounded-sm border border-zinc-200 p-3">
                          <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500 mb-1">
                            {label}
                          </div>
                          <div className="flex items-center justify-center h-16 w-16 mx-auto
                            rounded-full font-display font-bold text-2xl text-white"
                            style={{ backgroundColor: color }}>
                            {score ?? "—"}
                          </div>
                          {delta != null && (
                            <div className="text-center mt-1.5">
                              <span className={`text-[10px] font-mono ${
                                delta > 0 ? "text-emerald-700"
                                : delta < 0 ? "text-red-700"
                                : "text-zinc-500"}`}>
                                {delta > 0 && "▲"}{delta < 0 && "▼"}
                                {delta === 0 && "—"}{" "}
                                {delta !== 0 && `${delta > 0 ? "+" : ""}${delta}`}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Agentic Browsing — 2/3 style badge + audit checklist */}
                  {latestRun.agentic && latestRun.agentic.total > 0 && (
                    <div className="rounded-sm border border-zinc-200 p-3 mb-4"
                      data-testid="psi-agentic-card">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="h-8 w-8 rounded-sm bg-fuchsia-100 grid place-items-center">
                          <Zap className="h-4 w-4 text-fuchsia-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500">
                            Agentic Browsing · AI-agent readiness
                          </div>
                          <div className="text-[11px] text-zinc-600">
                            Passed {latestRun.agentic.passed}/{latestRun.agentic.total}
                            {" applicable checks — measures if AI agents (Gemini, ChatGPT,"}
                            {" Perplexity) can read + act on this page."}
                          </div>
                        </div>
                        <div className={`font-display font-bold text-2xl px-3 py-1 rounded-sm ${
                          latestRun.agentic.passed === latestRun.agentic.total
                            ? "bg-emerald-100 text-emerald-800"
                            : latestRun.agentic.passed >= latestRun.agentic.total / 2
                              ? "bg-amber-100 text-amber-800"
                              : "bg-red-100 text-red-800"}`}>
                          {latestRun.agentic.passed}/{latestRun.agentic.total}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {latestRun.agentic.checks.filter((c) => c.applicable).map((c) => (
                          <div key={c.id}
                            className="flex items-start gap-2 text-[11px] py-1">
                            <span className={`inline-block h-3 w-3 rounded-full mt-0.5 shrink-0 ${
                              c.status === "pass" ? "bg-emerald-500" : "bg-red-500"}`} />
                            <div className="min-w-0">
                              <div className="text-zinc-800">{c.title}</div>
                              {c.display_value && (
                                <div className="text-[10px] font-mono text-zinc-500">
                                  {c.display_value}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {latestRun.agentic.checks.some((c) => !c.applicable) && (
                        <details className="mt-2 text-[10px] font-mono">
                          <summary className="cursor-pointer text-zinc-500 hover:text-zinc-700">
                            {latestRun.agentic.checks.filter((c) => !c.applicable).length} not applicable
                          </summary>
                          <ul className="pl-4 pt-1 space-y-0.5">
                            {latestRun.agentic.checks.filter((c) => !c.applicable).map((c) => (
                              <li key={c.id} className="text-zinc-500">
                                • {c.title} <span className="opacity-50">({c.status})</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}

                  {/* Core Web Vitals metric strip */}
                  {latestRun.metrics && (
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-5">
                      {[
                        ["lcp", "LCP"], ["fcp", "FCP"], ["cls", "CLS"],
                        ["tbt", "TBT"], ["si", "Speed Index"], ["tti", "TTI"],
                      ].map(([mk, mlabel]) => {
                        const m = latestRun.metrics[mk] || {};
                        return (
                          <div key={mk} className="text-center rounded-sm bg-zinc-50 p-2">
                            <div className="text-[8px] uppercase tracking-wider font-mono text-zinc-500">
                              {mlabel}
                            </div>
                            <div className="font-mono font-semibold text-[12px] text-zinc-800 mt-0.5">
                              {m.display || "—"}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="text-[10px] text-zinc-500 font-mono mb-1">
                    Latest run: {new Date(latestRun.run_at).toLocaleString()}
                    {latestRun.source === "scheduled" && (
                      <Badge className="ml-2 rounded-sm text-[8px] font-mono bg-zinc-200 text-zinc-700">
                        SCHEDULED
                      </Badge>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-[12px] text-zinc-500 border border-dashed border-zinc-300 rounded-sm">
                  <Info className="h-4 w-4 inline mr-1" />
                  No {selectedStrategy} tests yet for this URL.
                  {(() => {
                    const other = selectedStrategy === "mobile" ? "desktop" : "mobile";
                    const otherExists = testsForUrl.some((t) => t.strategy === other);
                    if (otherExists) {
                      return (
                        <> Try the <button
                          onClick={() => setSelectedStrategy(other)}
                          className="underline font-semibold text-[#4285F4]"
                          data-testid="psi-empty-swap-btn">
                          {other}
                        </button> tab — data is available there.</>
                      );
                    }
                    return <> Click <strong>Run test</strong> above.</>;
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trend chart */}
          {chartData.length > 0 && (
            <Card className="rounded-sm border-zinc-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4 text-zinc-500" />
                  <div className="label-overline">
                    Score trend · {selectedStrategy} · {chartData.length} run(s)
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={chartData}
                    margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" />
                    <XAxis dataKey="ts" tick={{ fontSize: 10, fill: "#71717a" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#71717a" }} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 2 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Line type="monotone" dataKey="performance"
                      stroke="#4285F4" strokeWidth={2} dot={{ r: 3 }}
                      name="Performance" />
                    <Line type="monotone" dataKey="accessibility"
                      stroke="#34A853" strokeWidth={2} dot={{ r: 3 }}
                      name="Accessibility" />
                    <Line type="monotone" dataKey="best_practices"
                      stroke="#FBBC04" strokeWidth={2} dot={{ r: 3 }}
                      name="Best Practices" />
                    <Line type="monotone" dataKey="seo"
                      stroke="#EA4335" strokeWidth={2} dot={{ r: 3 }}
                      name="SEO" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Top opportunities on latest run */}
          {latestRun && (latestRun.opportunities || []).length > 0 && (
            <Card className="rounded-sm border-zinc-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="h-4 w-4 text-amber-600" />
                  <div className="label-overline">
                    Top opportunities & issues to fix ({selectedStrategy})
                  </div>
                </div>
                <div className="divide-y divide-zinc-100">
                  {latestRun.opportunities.map((o, i) => {
                    const c = scoreColor((o.score || 0) * 100);
                    return (
                      <div key={o.id || i} className="py-2.5"
                        data-testid={`psi-opportunity-${i}`}>
                        <div className="flex items-start gap-3">
                          <div className="h-5 w-5 rounded-sm shrink-0 mt-0.5"
                            style={{ backgroundColor: c }} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-[13px] text-zinc-900">
                                {o.title}
                              </span>
                              {o.savings_ms > 0 && (
                                <Badge className="rounded-sm text-[9px] font-mono bg-amber-100 text-amber-800">
                                  save ~{(o.savings_ms / 1000).toFixed(1)}s
                                </Badge>
                              )}
                              {o.display_value && (
                                <span className="text-[10px] font-mono text-zinc-500">
                                  {o.display_value}
                                </span>
                              )}
                            </div>
                            {o.description && (
                              <p className="text-[11px] text-zinc-600 mt-1 leading-relaxed">
                                <LighthouseDescription text={o.description} />
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-sm h-7 px-2.5 text-[10px] font-mono uppercase tracking-wider shrink-0 border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                            onClick={() => openAskAi(o)}
                            data-testid={`psi-ask-ai-btn-${i}`}
                          >
                            <Sparkles className="h-3 w-3 mr-1" />
                            Ask AI
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* History table */}
          {testsForUrl.length > 0 && (
            <Card className="rounded-sm border-zinc-200">
              <CardContent className="p-5">
                <div className="label-overline mb-3">
                  Full history · {testsForUrl.length} row(s)
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-left border-b border-zinc-200">
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">When</th>
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Strategy</th>
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider text-center">Perf</th>
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider text-center">A11y</th>
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider text-center">BP</th>
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider text-center">SEO</th>
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Source</th>
                        <th className="py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {testsForUrl.map((t) => (
                        <tr key={t.test_id} className="border-b border-zinc-100 hover:bg-zinc-50"
                          data-testid={`psi-history-row-${t.test_id}`}>
                          <td className="py-1.5 font-mono text-zinc-700">
                            {new Date(t.run_at).toLocaleString(undefined,
                              { day: "numeric", month: "short", year: "2-digit",
                                hour: "2-digit", minute: "2-digit" })}
                          </td>
                          <td className="py-1.5 font-mono">
                            <Badge variant="outline" className="rounded-sm text-[9px] font-mono">
                              {t.strategy}
                            </Badge>
                          </td>
                          {["performance", "accessibility", "best_practices", "seo"].map((k) => {
                            const s = t.scores?.[k];
                            return (
                              <td key={k} className="py-1.5 text-center">
                                <span className="inline-block h-6 w-6 rounded-full text-white
                                  font-mono font-bold text-[10px] leading-6"
                                  style={{ backgroundColor: scoreColor(s) }}>
                                  {s ?? "—"}
                                </span>
                              </td>
                            );
                          })}
                          <td className="py-1.5 font-mono text-zinc-500">
                            {t.source}
                          </td>
                          <td className="py-1.5 text-right">
                            <div className="inline-flex gap-1">
                              <button onClick={() => openTestDetail(t)}
                                className="text-[#002FA7] hover:bg-blue-50 rounded-sm px-1.5 py-1 font-mono text-[9px] uppercase tracking-wider"
                                data-testid={`psi-view-test-${t.test_id}`}>
                                View
                              </button>
                              <button onClick={() => deleteTest(t)}
                                className="text-red-600 hover:bg-red-50 rounded-sm p-1"
                                data-testid={`psi-delete-test-${t.test_id}`}>
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ── Historical scan details dialog ──────────────────────────────
       * Every row in the history table has a View button. Clicking one
       * re-fetches the full stored Lighthouse report for that scan (heavy,
       * so it's not shipped in the list endpoint) and renders scores,
       * metrics, all opportunities and diagnostics as they were on that
       * date. Use case: "What was the site actually saying on the day
       * before the redesign went live?"
       */}
      <Dialog
        open={detailTest !== null}
        onOpenChange={(o) => { if (!o) { setDetailTest(null); setDetailShowRaw(false); } }}
      >
        <DialogContent
          className="max-w-4xl rounded-sm border-zinc-200 max-h-[90vh] overflow-y-auto"
          data-testid="psi-history-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]">
              <Gauge className="h-4 w-4 text-[#002FA7]" />
              Historical scan
              {detailTest && (
                <Badge variant="outline" className="rounded-sm text-[9px] font-mono ml-1">
                  {detailTest.strategy}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-[12px] text-zinc-600 pt-1">
              {detailTest && (
                <>
                  <span className="font-mono">{detailTest.url}</span>{" "}
                  · run at{" "}
                  <span className="font-mono">
                    {new Date(detailTest.run_at).toLocaleString()}
                  </span>{" "}
                  · source <span className="font-mono">{detailTest.source}</span>
                  {detailTest.lighthouse_version && (
                    <> · Lighthouse v{detailTest.lighthouse_version}</>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="py-10 text-center text-[12px] text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin inline mr-1.5" />
              Loading full scan…
            </div>
          ) : detailTest && !detailTest._loading ? (
            <div className="space-y-4 text-[12px]">
              {/* Score circles */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {["performance", "accessibility", "best_practices", "seo"].map((k) => {
                  const s = detailTest.scores?.[k];
                  return (
                    <div key={k}
                         className="rounded-sm border border-zinc-200 p-3 text-center bg-white">
                      <div className="inline-block h-14 w-14 rounded-full text-white
                                      font-mono font-bold text-lg leading-[3.5rem]"
                           style={{ backgroundColor: scoreColor(s) }}>
                        {s ?? "—"}
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-1.5">
                        {CATEGORY_LABELS[k] || k}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Core Web Vitals + metrics */}
              {detailTest.metrics && Object.keys(detailTest.metrics).length > 0 && (
                <div className="rounded-sm border border-zinc-200 p-3">
                  <div className="label-overline mb-2">Core Web Vitals · this run</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(detailTest.metrics).map(([k, m]) => (
                      <div key={k} className="rounded-sm bg-zinc-50 px-2 py-1.5">
                        <div className="font-mono text-[9px] uppercase text-zinc-500 tracking-wider">
                          {k.replace(/_/g, " ")}
                        </div>
                        <div className="font-mono font-bold">
                          {m?.display_value || m?.value || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Opportunities & diagnostics as-of-this-run */}
              {(detailTest.opportunities || []).length > 0 && (
                <div className="rounded-sm border border-zinc-200 p-3">
                  <div className="label-overline mb-2">
                    Opportunities & diagnostics ({detailTest.opportunities.length})
                  </div>
                  <div className="space-y-2">
                    {detailTest.opportunities.map((o, i) => (
                      <div key={o.id || i}
                           className="rounded-sm bg-zinc-50 px-3 py-2 border border-zinc-100"
                           data-testid={`psi-detail-opp-${i}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold">{o.title}</div>
                            {o.display_value && (
                              <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                                {o.display_value}
                              </div>
                            )}
                          </div>
                          {o.savings_ms != null && (
                            <div className="font-mono text-[10px] text-amber-700 bg-amber-50 rounded-sm px-1.5 py-0.5 whitespace-nowrap">
                              save {Math.round(o.savings_ms)} ms
                            </div>
                          )}
                        </div>
                        {o.description && (
                          <div className="mt-1 text-[11px] text-zinc-600 leading-relaxed">
                            <LighthouseDescription text={o.description} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Every audit that Lighthouse ran — sourced from the raw
                  report so nothing gets lost between the summary and the
                  full picture. */}
              {detailTest.lighthouse_result?.audits && (
                <div className="rounded-sm border border-zinc-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-overline">
                      All Lighthouse audits ({Object.keys(detailTest.lighthouse_result.audits).length})
                    </div>
                    <button className="text-[10px] font-mono uppercase tracking-wider text-[#002FA7] hover:underline"
                            onClick={() => setDetailShowRaw((v) => !v)}>
                      {detailShowRaw ? "Hide raw JSON" : "Show raw JSON"}
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1 pr-2">
                    {Object.values(detailTest.lighthouse_result.audits)
                      .sort((a, b) => {
                        // Failed / non-passing audits float to the top so
                        // the historical view highlights what wasn't
                        // ideal on that date.
                        const sa = a.score == null ? 0.5 : a.score;
                        const sb = b.score == null ? 0.5 : b.score;
                        return sa - sb;
                      })
                      .map((a) => {
                        const passing = a.score != null && a.score >= 0.9;
                        const failing = a.score != null && a.score < 0.5;
                        return (
                          <div key={a.id}
                               className="flex items-start gap-2 rounded-sm px-2 py-1 hover:bg-zinc-50">
                            <div className={`mt-0.5 h-2 w-2 rounded-full flex-shrink-0 ${
                              passing ? "bg-emerald-500"
                                : failing ? "bg-red-500"
                                : "bg-amber-500"}`} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-[11px]">{a.title}</span>
                                {a.displayValue && (
                                  <span className="font-mono text-[10px] text-zinc-500">
                                    · {a.displayValue}
                                  </span>
                                )}
                              </div>
                              <div className="font-mono text-[9px] text-zinc-400">
                                {a.id}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Optional raw JSON — for the geeks / support cases */}
              {detailShowRaw && detailTest.lighthouse_result && (
                <div className="rounded-sm border border-zinc-200 bg-zinc-900 text-emerald-200 p-3">
                  <div className="label-overline text-emerald-400 mb-2">
                    Raw Lighthouse result (JSON)
                  </div>
                  <pre className="text-[10px] leading-tight overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(detailTest.lighthouse_result, null, 2)}
                  </pre>
                </div>
              )}

              {!detailTest.lighthouse_result && (
                <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800 flex items-start gap-2">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    This scan was captured before we started storing the full
                    Lighthouse report. Only the summary metrics above are
                    available for it. All new scans (manual and scheduled)
                    now keep the full report on file automatically.
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailTest(null)}
                    className="rounded-sm">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Ask-AI dialog ────────────────────────────────────────────── */}
      <Dialog
        open={askIssue !== null}
        onOpenChange={(o) => { if (!o) { setAskIssue(null); setAskFocus(null); setAskAnswer(""); setAskError(""); } }}
      >
        <DialogContent
          className="max-w-2xl rounded-sm border-zinc-200 max-h-[85vh] overflow-y-auto"
          data-testid="psi-ask-ai-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]">
              <Sparkles className="h-4 w-4 text-violet-600" />
              Ask AI about this issue with a focus on
            </DialogTitle>
            {askIssue && (
              <DialogDescription className="text-[12px] text-zinc-600 pt-1">
                <span className="font-semibold text-zinc-800">{askIssue.title}</span>
                {askIssue.display_value && (
                  <span className="ml-2 font-mono text-[10px] text-zinc-500">
                    ({askIssue.display_value})
                  </span>
                )}
              </DialogDescription>
            )}
          </DialogHeader>

          {/* Two-button choice (shown until user picks a focus) */}
          {!askFocus && (
            <div className="grid grid-cols-2 gap-3 py-4">
              <Button
                variant="outline"
                className="rounded-sm h-24 flex-col gap-1.5 border-blue-200 hover:border-blue-400 hover:bg-blue-50"
                onClick={() => runAskAi("wordpress")}
                data-testid="psi-ask-ai-focus-wordpress"
              >
                <div className="text-[13px] font-semibold text-blue-800">WordPress</div>
                <div className="text-[10px] text-blue-700 opacity-80">
                  Plugin, theme &amp; caching fixes
                </div>
              </Button>
              <Button
                variant="outline"
                className="rounded-sm h-24 flex-col gap-1.5 border-zinc-300 hover:border-zinc-500 hover:bg-zinc-50"
                onClick={() => runAskAi("general")}
                data-testid="psi-ask-ai-focus-general"
              >
                <div className="text-[13px] font-semibold text-zinc-800">General info</div>
                <div className="text-[10px] text-zinc-600 opacity-80">
                  Framework-agnostic fixes
                </div>
              </Button>
            </div>
          )}

          {/* Answer area */}
          {askFocus && (
            <div className="mt-1 min-h-[120px]">
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-zinc-100">
                <Badge
                  className={`rounded-sm text-[9px] font-mono uppercase ${
                    askFocus === "wordpress"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-zinc-100 text-zinc-800"
                  }`}
                >
                  Focus · {askFocus === "wordpress" ? "WordPress" : "General"}
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-sm h-6 px-2 text-[10px] ml-auto"
                  onClick={() => { setAskFocus(null); setAskAnswer(""); setAskError(""); }}
                  data-testid="psi-ask-ai-back"
                >
                  ← Change focus
                </Button>
              </div>

              {askLoading && (
                <div className="flex items-center gap-2 text-[12px] text-zinc-600 py-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Asking Claude for a {askFocus === "wordpress" ? "WordPress" : "general"}-focused answer…
                </div>
              )}
              {askError && !askLoading && (
                <div className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-sm p-3">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <div>{askError}</div>
                </div>
              )}
              {askAnswer && !askLoading && (
                <div data-testid="psi-ask-ai-answer">
                  <AiMarkdown text={askAnswer} />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => { setAskIssue(null); setAskFocus(null); setAskAnswer(""); setAskError(""); }}
              data-testid="psi-ask-ai-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
